import { InlineAlert } from '../../components/BaseComponents'
import { useState, useEffect } from "react";
import { Button, LoadingSpinner } from "../../components/BaseComponents";
import CanonicalModal from "../../components/BaseComponents/CanonicalModal/CanonicalModal";
import Input from "../../components/BaseComponents/Forms/Input";
import SearchableSelect, { SearchableSelectOption } from "../../components/BaseComponents/Forms/SearchableSelect";
import useApi from "../../hooks/useApi";
import useCleanup from "../../hooks/useCleanup";
import { getApiErrorMessage, getCSRFToken } from "../../lib/utils";

type EditTrainingUserModalProps = {
	trainingSessionId: string;
	sessionData?: any;
	user: any;
	onClose: () => void;
	onUpdated: () => void;
	isOpen?: boolean;
};

// Sub-task I-extra2 §8.1/§8.4 (docs/todo/user-org-membership-i-extra2-training-org-reassignment.md):
// a brand-new, not-yet-created org typed into the combo-box is tracked with this sentinel prefix
// rather than a real companyId, so `update()` below can tell "move to this existing org" apart
// from "create a new org with this name and move there" when it calls the reassignment endpoint.
const NEW_ORG_PREFIX = '__new__:';

type OrganisationOption = SearchableSelectOption<string>;

function EditTrainingUserModal({ trainingSessionId, sessionData: parentSessionData, user, onClose, onUpdated, isOpen }: EditTrainingUserModalProps) {
	const [email, setEmail] = useState('');
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');
	const [sessionData, setSessionData] = useState<any>(parentSessionData);
	const [organisationOptions, setOrganisationOptions] = useState<OrganisationOption[]>([]);
	const [selectedOrganisation, setSelectedOrganisation] = useState<OrganisationOption | null>(null);
	const api = useApi();

	const reset = () => {
		if (user) {
			setEmail(user.email || '');
			setError('');
		}
	};

	const loadSessionData = useCleanup(() => {
		if (!isOpen) {
			return;
		}
		let cancelled = false;
		const run = async () => {
			try {
				const result = await api.get(`admin/training-sessions/${trainingSessionId}`);
				if (cancelled) {
					return;
				}
				setSessionData(result.data);
			} catch (err) {
				// Silently fail if session data can't be loaded; display fallback title
				if (!cancelled) {
					console.error('Failed to load session data:', err);
				}
			}
		}
		run();
		return () => {
			cancelled = true;
		}
	})

	const loadOrganisations = useCleanup(() => {
		if (!isOpen) {
			return;
		}
		let cancelled = false;
		const run = async () => {
			try {
				const result = await api.get(`admin/training-sessions/${trainingSessionId}/organisations`);
				if (cancelled) {
					return;
				}
				// Belt-and-braces (QA fix item 2): blank-named orgs are legacy data (shouldn't be
				// reachable going forward), but filter them out here regardless so an admin can never
				// accidentally reassign a trainee onto an unnamed/unusable organisation.
				const options: OrganisationOption[] = (result.data.organisations || [])
					.filter((org: any) => org.name && org.name.trim() !== '')
					.map((org: any) => ({
						value: org.id,
						label: org.name,
					}));
				setOrganisationOptions(options);
			} catch (err) {
				// Silently fail if the org list can't be loaded; the combo-box simply shows no
				// options yet (the admin can still type a brand-new org name via `creatable`).
				if (!cancelled) {
					console.error('Failed to load training session organisations:', err);
				}
			}
		}
		run();
		return () => {
			cancelled = true;
		}
	})

	useEffect(() => {
		if (isOpen && user) {
			setEmail(user.email || '');
			setError('');
			// A blank-named org (filtered out of the selectable options list above) is treated
			// as "no real organisation assigned" here too - otherwise this would pre-select an
			// option whose label is an empty string, which renders as an empty-looking combo-box
			// (hasValue=true so react-select never shows the placeholder) instead of genuinely
			// looking unassigned.
			setSelectedOrganisation(
				user.companyId && user.organisationName && user.organisationName.trim() !== ''
					? { value: user.companyId, label: user.organisationName }
					: null
			);
		}
		loadSessionData();
		loadOrganisations();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [trainingSessionId, isOpen, user])

	// Sync with parent sessionData when it changes (e.g. admin selects a different
	// session from the dropdown while this modal's data was loaded for the old one).
	useEffect(() => {
		if (parentSessionData) {
			setSessionData(parentSessionData);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [parentSessionData])

	// Email edits only apply to pending invitations - `updateTrainingUser()` on the backend
	// explicitly rejects a signed-up `User` row ("Can only edit pending..."), since their email
	// is managed via their own account, not this admin flow. Organisation reassignment (below)
	// works for both row types.
	const isSignedUpUser = user?.type === 'user';

	const update = async () => {
		setLoading(true);
		setError('');
		try {
			const csrfToken = await getCSRFToken();
			if (!isSignedUpUser) {
				await api.put(`/admin/training-sessions/${trainingSessionId}/users/${user.id}`, {
					email,
					_csrf: csrfToken,
				})
			}

			// Only reassign when the admin actually changed the organisation selection - this is
			// a separate write from the email update above (§4's deliberate split between "edit
			// this person" and "which org is this person in").
			const originalCompanyId = user.companyId || null;
			const isNewOrg = selectedOrganisation?.value?.startsWith(NEW_ORG_PREFIX);
			const orgChanged = selectedOrganisation && (isNewOrg || selectedOrganisation.value !== originalCompanyId);
			if (orgChanged) {
				await api.put(`/admin/training-sessions/${trainingSessionId}/users/${user.id}/organisation`, {
					...(isNewOrg
						? { organisationName: selectedOrganisation!.label }
						: { organisationId: selectedOrganisation!.value }),
					_csrf: csrfToken,
				})
			}

			onUpdated();
		} catch (err: any) {
			setError(getApiErrorMessage(err, 'Failed to update this user.'));
		} finally {
			setLoading(false);
		}
	}


	const formatSessionDisplay = () => {
		if (!sessionData?.name) return '';
		let display = sessionData.name;
		if (sessionData.start_date) {
			const date = new Date(sessionData.start_date);
			const formatted = date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' });
			display += ` (starts: ${formatted})`;
		}
		return display;
	}

	return (
		<CanonicalModal
			open={!!isOpen}
			onClose={() => { reset(); onClose(); }}
			size="small"
			title="Edit training user"
			subtitle={sessionData?.name ? formatSessionDisplay() : undefined}
			actions={
				<>
					<Button
						onClick={update}
						additionalClass="mr-5"
						disabled={(!isSignedUpUser && !email) || loading}
						type="Primary"
						text="Update user"
					/>
					<Button onClick={onClose} type="Outlined" text="Cancel" />
				</>
			}
		>
			<Input
				label="Email"
				name="email"
				additionalClass="mt-8"
				value={email}
				onChange={e => setEmail(e.target.value)}
				type="email"
				placeholder="name@example.com..."
				disabled={isSignedUpUser}
			/>
			{ isSignedUpUser && (
				<span className="block text-xs text-charcoal-100 mt-1">
					This user has already signed up, their email can only be changed from their own account.
				</span>
			) }
			<label className="block mt-4">
				<span className="block text-sm text-charcoal-100 my-1">Organisation</span>
				<SearchableSelect<OrganisationOption>
					creatable
					fillContainer
					placeholder="Select an organisation (or create a new one)"
					options={organisationOptions}
					value={selectedOrganisation}
					onChange={(option) => setSelectedOrganisation(option as OrganisationOption | null)}
					onCreateOption={(inputValue) => {
						const newOption: OrganisationOption = { value: `${NEW_ORG_PREFIX}${inputValue}`, label: inputValue };
						setOrganisationOptions((prev) => [...prev, newOption]);
						setSelectedOrganisation(newOption);
					}}
					formatCreateLabel={(inputValue) => `Create "${inputValue}"`}
					isClearable={false}
				/>
			</label>
			{ loading && <LoadingSpinner /> }
			{ error && (
				<InlineAlert type='error' className='mt-4'>{ error }
				</InlineAlert>
			)}
		</CanonicalModal>
	)
}

export default EditTrainingUserModal
